// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import { consoleService } from '../../services/consoleService';
import { RuntimeModule, RuntimeInfo } from '../../types/modules';
import { authService } from '../../services/authService';
import { UserRole } from '../../types/auth';
import './RuntimeModules.scss';

const RuntimeModules: React.FC = () => {
  const [modules, setModules] = useState<RuntimeModule[]>([]);
  const [runtimeInfo, setRuntimeInfo] = useState<RuntimeInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const userRole = authService.getSessionRole();
  const canViewModules = userRole === UserRole.USER_ROLE_ADMIN;

  useEffect(() => {
    if (canViewModules) {
      loadModules();
      loadRuntimeInfo();
    } else {
      setLoading(false);
    }
  }, [canViewModules]);

  const loadModules = async (isRefresh = false) => {
    try {
      setError('');
      if (isRefresh) {
        setRefreshing(true);
      }
      const data = await consoleService.listRuntimeModules();
      setModules(data.modules || []);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load runtime modules');
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      }
    }
  };

  const loadRuntimeInfo = async () => {
    try {
      const data = await consoleService.getRuntimeInfo();
      setRuntimeInfo(data);
    } catch (err: any) {
      console.error('Failed to load runtime info:', err);
      // Don't set error for runtime info as it's not critical
    } finally {
      setLoading(false);
    }
  };

  const getFilteredModules = (): RuntimeModule[] => {
    if (!searchTerm) return modules;

    return modules.filter(module =>
      module.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      module.path.toLowerCase().includes(searchTerm.toLowerCase()) ||
      module.version.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const truncateText = (text: string, maxLength: number = 50) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const getModuleTypeIcon = (path: string) => {
    if (path.endsWith('.lua')) {
      return <i className="fab fa-lua text-primary" title="Lua Module"></i>;
    } else if (path.endsWith('.js') || path.endsWith('.ts')) {
      return <i className="fab fa-js-square text-warning" title="JavaScript/TypeScript Module"></i>;
    } else if (path.endsWith('.go')) {
      return <i className="fas fa-code text-info" title="Go Module"></i>;
    }
    return <i className="fas fa-file-code text-secondary" title="Code Module"></i>;
  };

  if (!canViewModules) {
    return (
      <div className="runtime-modules-container">
        <h2 className="pb-4">Runtime Modules</h2>
        <div className="alert alert-warning">
          <strong>Access Denied:</strong> You don't have permission to view runtime modules. Developer role required.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="runtime-modules-container">
        <h2 className="pb-4">Runtime Modules</h2>
        <div className="alert alert-info">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <strong>Loading runtime modules...</strong>
        </div>
      </div>
    );
  }

  return (
    <div className="runtime-modules-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Runtime Modules</h2>
        <button
          className="btn btn-outline-primary"
          onClick={() => {
            loadModules(true);
            loadRuntimeInfo();
          }}
          disabled={loading || refreshing}
          title="Refresh modules"
        >
          {refreshing ? (
            <>
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              Refreshing...
            </>
          ) : (
            <>
              <i className="fas fa-sync-alt me-2"></i>
              Refresh
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="alert alert-danger mb-3">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Runtime Information */}
      {runtimeInfo && (
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-info-circle me-2"></i>
              Runtime Information
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-3">
                <strong>Runtime:</strong>
                <div className="text-muted">{runtimeInfo.name}</div>
              </div>
              <div className="col-md-3">
                <strong>Version:</strong>
                <div className="text-muted">{runtimeInfo.version}</div>
              </div>
              {runtimeInfo.lua_version && (
                <div className="col-md-3">
                  <strong>Lua Version:</strong>
                  <div className="text-muted">{runtimeInfo.lua_version}</div>
                </div>
              )}
              {runtimeInfo.node_version && (
                <div className="col-md-3">
                  <strong>Node.js Version:</strong>
                  <div className="text-muted">{runtimeInfo.node_version}</div>
                </div>
              )}
              {runtimeInfo.go_version && (
                <div className="col-md-3">
                  <strong>Go Version:</strong>
                  <div className="text-muted">{runtimeInfo.go_version}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <label className="form-label">Search Modules</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search by name, path, or version..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-6 d-flex align-items-end">
              <div className="text-muted">
                <strong>Total Modules:</strong> {modules.length}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modules Table */}
      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th style={{ width: '5%' }}>Type</th>
                  <th style={{ width: '25%' }}>Name</th>
                  <th style={{ width: '40%' }}>Path</th>
                  <th style={{ width: '15%' }}>Version</th>
                  <th style={{ width: '15%' }}>Checksum</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredModules().map((module, index) => (
                  <tr key={`${module.name}-${index}`}>
                    <td className="text-center">
                      {getModuleTypeIcon(module.path)}
                    </td>
                    <td>
                      <strong>{module.name}</strong>
                    </td>
                    <td>
                      <code className="module-path" title={module.path}>
                        {truncateText(module.path, 60)}
                      </code>
                    </td>
                    <td>
                      <span className="badge bg-info">{module.version}</span>
                    </td>
                    <td>
                      <code className="module-sum" title={module.sum}>
                        {truncateText(module.sum, 12)}
                      </code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {getFilteredModules().length === 0 && (
            <div className="text-center py-4">
              <p className="text-muted">
                {searchTerm ? 'No modules match your search.' : 'No runtime modules found.'}
              </p>
              {!searchTerm && modules.length === 0 && (
                <div className="alert alert-info mt-3">
                  <i className="fas fa-info-circle me-2"></i>
                  No runtime modules are currently loaded. This could mean:
                  <ul className="mb-0 mt-2 text-start">
                    <li>No modules have been deployed to the server</li>
                    <li>The runtime is not configured to load modules</li>
                    <li>There was an error loading the modules</li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Module Statistics */}
      {modules.length > 0 && (
        <div className="row mt-4">
          <div className="col-md-4">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title text-primary">{modules.length}</h5>
                <p className="card-text">Total Modules</p>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title text-success">
                  {modules.filter(m => m.path.endsWith('.lua')).length}
                </h5>
                <p className="card-text">Lua Modules</p>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title text-warning">
                  {modules.filter(m => m.path.endsWith('.js') || m.path.endsWith('.ts')).length}
                </h5>
                <p className="card-text">JS/TS Modules</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RuntimeModules;
